import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Eye, Info, X } from 'lucide-react';
import { Card, CardContent } from '../../../../components/ui/Card';
import { ComplianceEventView } from '../../../../models/payroll';

interface EventLogTabProps {
  history: ComplianceEventView[];
  onResolve: (eventId: number) => void;
}

const severityClass: Record<string, string> = {
  Critical: 'bg-red-100 text-red-700',
  Error: 'bg-red-100 text-red-700',
  High: 'bg-orange-100 text-orange-700',
  Warning: 'bg-amber-100 text-amber-700',
  Medium: 'bg-amber-100 text-amber-700',
  Low: 'bg-blue-100 text-blue-700',
  Info: 'bg-gray-100 text-gray-700',
};

export const EventLogTab: React.FC<EventLogTabProps> = ({ history, onResolve }) => {
  const { t } = useTranslation();
  const [severityFilter, setSeverityFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'resolved' | 'unresolved'>('all');
  const [selectedEvent, setSelectedEvent] = useState<ComplianceEventView | null>(null);
  const [confirmResolveId, setConfirmResolveId] = useState<number | null>(null);

  const filtered = useMemo(() => {
    return history.filter(e => {
      if (severityFilter !== 'all' && e.severity !== severityFilter) return false;
      if (statusFilter === 'resolved' && !e.isResolved) return false;
      if (statusFilter === 'unresolved' && e.isResolved) return false;
      return true;
    });
  }, [history, severityFilter, statusFilter]);

  const handleResolveClick = (eventId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setConfirmResolveId(eventId);
  };

  const confirmResolve = () => {
    if (confirmResolveId !== null) {
      onResolve(confirmResolveId);
      setConfirmResolveId(null);
      if (selectedEvent?.id === confirmResolveId) setSelectedEvent(null);
    }
  };

  return (
    <>
      {/* Explainer banner */}
      <div className="mb-4 p-3 rounded-lg bg-blue-50 border border-blue-200 text-blue-900 flex items-start gap-3">
        <Info className="w-4 h-4 mt-0.5 shrink-0 text-blue-600" />
        <div className="flex-1 text-sm">
          <p className="font-medium">
            {t(
              'payroll.complianceCenter.eventLog.explainerTitle',
              'About the Event Log'
            )}
          </p>
          <p className="text-blue-800/90 mt-0.5">
            {t(
              'payroll.complianceCenter.eventLog.explainerBody',
              'This is a chronological audit trail of compliance activity (runs, breaches, fixes). Click any row to read the full detail. The "Mark Resolved" button is an HR acknowledgement only. It confirms you have reviewed and addressed the event in the relevant module. It does not auto-correct the underlying data.'
            )}
          </p>
        </div>
      </div>

      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
            <h3 className="font-semibold">
              {t('payroll.compliance.eventHistory', 'Compliance Event History')}
            </h3>
            <div className="flex gap-2 flex-wrap">
              <select
                value={severityFilter}
                onChange={e => setSeverityFilter(e.target.value)}
                className="text-sm px-2 py-1 border border-input rounded-md bg-background"
              >
                <option value="all">
                  {t('payroll.complianceCenter.eventLog.allSeverities', 'All severities')}
                </option>
                <option value="Error">{t('payroll.compliance.severities.error', 'Error')}</option>
                <option value="Warning">{t('payroll.compliance.severities.warning', 'Warning')}</option>
                <option value="Info">{t('payroll.compliance.severities.info', 'Info')}</option>
              </select>
              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value as 'all' | 'resolved' | 'unresolved')}
                className="text-sm px-2 py-1 border border-input rounded-md bg-background"
              >
                <option value="all">
                  {t('payroll.complianceCenter.eventLog.allStatuses', 'All statuses')}
                </option>
                <option value="unresolved">
                  {t('payroll.compliance.unresolved', 'Unresolved')}
                </option>
                <option value="resolved">
                  {t('payroll.compliance.resolved', 'Resolved')}
                </option>
              </select>
            </div>
          </div>
          {filtered.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              {t('payroll.compliance.noEvents', 'No compliance events recorded.')}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-muted-foreground">
                    <th className="text-start py-2 font-medium">{t('common.date', 'Date')}</th>
                    <th className="text-start py-2 font-medium">
                      {t('payroll.compliance.title', 'Title')}
                    </th>
                    <th className="text-start py-2 font-medium">
                      {t('payroll.compliance.severity', 'Severity')}
                    </th>
                    <th className="text-start py-2 font-medium">
                      {t('payroll.compliance.detail', 'Detail')}
                    </th>
                    <th className="text-start py-2 font-medium">
                      {t('common.status', 'Status')}
                    </th>
                    <th className="text-start py-2 font-medium">
                      {t('common.actions', 'Actions')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(event => (
                    <tr
                      key={event.id}
                      className="border-b border-border last:border-0 cursor-pointer hover:bg-muted/40 transition-colors"
                      onClick={() => setSelectedEvent(event)}
                      title={t(
                        'payroll.complianceCenter.eventLog.rowTooltip',
                        'Click to see full details'
                      )}
                    >
                      <td className="py-2">
                        {new Date(event.createdOn).toLocaleDateString()}
                      </td>
                      <td className="py-2">
                        <span className="inline-flex items-center gap-1.5">
                          <Eye className="w-3.5 h-3.5 text-muted-foreground/70" />
                          {event.title}
                        </span>
                      </td>
                      <td className="py-2">
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            severityClass[event.severity] ?? 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {event.severity}
                        </span>
                      </td>
                      <td className="py-2 max-w-xs truncate text-muted-foreground">
                        {event.detail}
                      </td>
                      <td className="py-2">
                        <span
                          className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            event.isResolved
                              ? 'bg-green-100 text-green-700'
                              : 'bg-red-100 text-red-700'
                          }`}
                        >
                          {event.isResolved
                            ? t('payroll.compliance.resolved', 'Resolved')
                            : t('payroll.compliance.unresolved', 'Unresolved')}
                        </span>
                      </td>
                      <td className="py-2">
                        {!event.isResolved && (
                          <button
                            onClick={e => handleResolveClick(event.id, e)}
                            className="text-xs px-2 py-1 bg-primary text-primary-foreground rounded hover:bg-primary/90 transition-colors inline-flex items-center gap-1"
                            title={t(
                              'payroll.complianceCenter.eventLog.resolveTooltip',
                              'Acknowledgement only. Clicking this marks the event as reviewed by HR. It does NOT automatically fix the underlying issue. Address the root cause in the relevant module first, then mark resolved here.'
                            )}
                          >
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            {t('payroll.compliance.markResolved', 'Mark Resolved')}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Event detail modal */}
      {selectedEvent && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          onClick={() => setSelectedEvent(null)}
        >
          <div
            className="bg-card border border-border rounded-xl shadow-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-border flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      severityClass[selectedEvent.severity] ?? 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {selectedEvent.severity}
                  </span>
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      selectedEvent.isResolved
                        ? 'bg-green-100 text-green-700'
                        : 'bg-red-100 text-red-700'
                    }`}
                  >
                    {selectedEvent.isResolved
                      ? t('payroll.compliance.resolved', 'Resolved')
                      : t('payroll.compliance.unresolved', 'Unresolved')}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {selectedEvent.eventType}
                  </span>
                </div>
                <h3 className="font-semibold text-base">{selectedEvent.title}</h3>
              </div>
              <button
                type="button"
                onClick={() => setSelectedEvent(null)}
                className="p-1 rounded-md hover:bg-muted text-muted-foreground"
                aria-label={t('common.close', 'Close')}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-5 py-4 space-y-4">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                  {t('payroll.compliance.detail', 'Detail')}
                </p>
                <p className="text-sm whitespace-pre-wrap break-words">
                  {selectedEvent.detail}
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                    {t('payroll.complianceCenter.eventLog.createdOn', 'Created on')}
                  </p>
                  <p>{new Date(selectedEvent.createdOn).toLocaleString()}</p>
                </div>
                {selectedEvent.resolvedOn && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                      {t('payroll.complianceCenter.eventLog.resolvedOn', 'Resolved on')}
                    </p>
                    <p>{new Date(selectedEvent.resolvedOn).toLocaleString()}</p>
                  </div>
                )}
                {selectedEvent.userId && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                      {t('payroll.complianceCenter.eventLog.relatedUser', 'Related employee ID')}
                    </p>
                    <p className="font-mono text-xs break-all">{selectedEvent.userId}</p>
                  </div>
                )}
              </div>
            </div>
            <div className="px-5 py-3 border-t border-border flex flex-wrap items-center justify-between gap-2 bg-muted/20">
              <p className="text-xs text-muted-foreground inline-flex items-center gap-1.5">
                <Info className="w-3.5 h-3.5" />
                {t(
                  'payroll.complianceCenter.eventLog.resolveHint',
                  'Marking this resolved is acknowledgement only. It does not auto-correct the issue.'
                )}
              </p>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setSelectedEvent(null)}
                  className="px-3 py-1.5 text-sm border border-input rounded-md hover:bg-muted"
                >
                  {t('common.close', 'Close')}
                </button>
                {!selectedEvent.isResolved && (
                  <button
                    type="button"
                    onClick={() => setConfirmResolveId(selectedEvent.id)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    {t('payroll.compliance.markResolved', 'Mark Resolved')}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Resolve confirmation */}
      {confirmResolveId !== null && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-card border border-border rounded-xl shadow-xl w-full max-w-md">
            <div className="px-5 py-4 border-b border-border flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              <h3 className="font-semibold">
                {t('payroll.complianceCenter.eventLog.resolveConfirmTitle', 'Mark event as resolved?')}
              </h3>
            </div>
            <div className="px-5 py-4 text-sm space-y-2">
              <p>
                {t(
                  'payroll.complianceCenter.eventLog.resolveConfirmBody',
                  'This is an acknowledgement that you have reviewed the event and addressed the underlying issue in the relevant module (Payroll, Pension, Settings, etc.).'
                )}
              </p>
              <p className="text-muted-foreground">
                {t(
                  'payroll.complianceCenter.eventLog.resolveConfirmWarning',
                  'It will NOT automatically correct any data, regenerate contributions, or undo a breach. Make sure the root cause is fixed first.'
                )}
              </p>
            </div>
            <div className="px-5 py-3 border-t border-border flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmResolveId(null)}
                className="px-3 py-1.5 text-sm border border-input rounded-md hover:bg-muted"
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                type="button"
                onClick={confirmResolve}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-emerald-600 text-white rounded-md hover:bg-emerald-700"
              >
                <CheckCircle2 className="w-4 h-4" />
                {t('payroll.compliance.markResolved', 'Mark Resolved')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
